import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Logo } from "@/components/site/Header";
import { Reveal } from "@/components/site/Reveal";
import { PALETTE, REG_STEPS, ADMIN_NAV, KPIS, ARCH, DATA_MODEL, SECURITY, STRIPE_FLOW, PHASES, NEEDED } from "@/data/proposal";

const Block = ({ id, n, title, children }) => (
  <section id={id} data-testid={`proposal-${id}`} className="py-20 md:py-28 border-t border-white/10 scroll-mt-24">
    <Reveal className="mb-12">
      <p className="font-mono-epl text-sm accent-text">{n}</p>
      <h2 className="mt-3 h2-epl">{title}</h2>
    </Reveal>
    {children}
  </section>
);

const Brand = () => (
  <div className="grid lg:grid-cols-2 gap-10">
    <div>
      <p className="text-slate-300 leading-relaxed">Direction: <strong className="text-white">"Night Session"</strong> — modern sports editorial. A midnight base, a single kinetic volt accent for action, soft rounded glass surfaces, and expressive Syne display type paired with clean Manrope body text for parents. Real training photography, ambient light, slow cinematic zoom. Minimal motion, maximum clarity.</p>
      <div className="mt-8 space-y-4">
        <p className="font-display text-5xl font-extrabold tracking-tight">Syne — display</p>
        <p className="text-lg">Manrope — body & forms</p>
        <p className="font-mono-epl accent-text">IBM Plex Mono — numbers · $750.00</p>
      </div>
    </div>
    <div className="grid grid-cols-5 gap-2">
      {PALETTE.map((c) => (
        <div key={c.h} data-testid={`palette-${c.n}`}>
          <div className="aspect-[1/3] rounded-2xl border border-white/10" style={{ background: c.h }} />
          <p className="mt-3 text-xs font-bold uppercase">{c.n}</p>
          <p className="font-mono-epl text-[10px] text-slate-500">{c.h}</p>
          <p className="text-[10px] text-slate-500">{c.u}</p>
        </div>
      ))}
    </div>
  </div>
);

const Registration = () => (
  <>
    <div className="grid md:grid-cols-5 gap-3">
      {REG_STEPS.map((s) => (
        <div key={s.n} className="rounded-3xl bg-white/[0.03] border border-white/10 p-6" data-testid={`reg-step-${s.n}`}>
          <p className="font-mono-epl text-xs accent-text">STEP {s.n}</p>
          <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{s.t}</h3>
          <ul className="mt-4 space-y-1.5 text-sm text-slate-400">{s.f.map((f) => <li key={f}>— {f}</li>)}</ul>
          <p className="mt-5 text-xs text-slate-300 border-t border-white/10 pt-4">{s.note}</p>
        </div>
      ))}
    </div>
    <div className="mt-6 grid md:grid-cols-3 gap-6 text-sm">
      <div className="rounded-3xl border border-[var(--accent)]/30 p-6"><p className="font-display text-xl font-bold tracking-tight accent-text">Siblings</p><p className="mt-2 text-slate-400">"Add another player" repeats Steps 1, 3 and 4 per child; parent details are kept. Each child gets their own group, medical record, consent and size. $750 each, no automatic discount, each counted against 110.</p></div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"><p className="font-display text-xl font-bold tracking-tight">Mobile-first</p><p className="mt-2 text-slate-400">One question group per screen, progress bar, autosave of the draft in the browser, large tap targets, native date & select pickers.</p></div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"><p className="font-display text-xl font-bold tracking-tight">After payment</p><p className="mt-2 text-slate-400">One family receipt + one confirmation per player (registration number, group, dates, venue, clothing, water bottle, scheduling note, QR check-in code). Parent portal access link included.</p></div>
    </div>
  </>
);

const Admin = () => (
  <div data-testid="admin-mock" className="rounded-[2rem] overflow-hidden border border-white/10 bg-[#0f1420] grid md:grid-cols-12 min-h-[480px]">
    <aside className="md:col-span-3 border-r border-white/10 p-5">
      <div className="flex items-center gap-3 mb-8"><Logo size={32} /><span className="font-display font-bold tracking-tight text-sm">EPL Admin</span></div>
      {ADMIN_NAV.map((n, i) => <p key={n} className={`py-2 px-3 rounded-xl text-sm ${i === 0 ? "bg-[var(--accent)] text-[#070a11] font-semibold" : "text-slate-400"}`}>{n}</p>)}
    </aside>
    <div className="md:col-span-9 p-6 md:p-8">
      <div className="flex justify-between items-center"><p className="font-display text-2xl font-bold tracking-tight">Adelaide · Dec 2026</p><span className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500">Layout mock</span></div>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-6 gap-3">
        {KPIS.map((k) => <div key={k.k} className="rounded-2xl border border-white/10 p-4"><p className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500">{k.k}</p><p className="mt-2 font-mono-epl text-xl accent-text">{k.v}</p></div>)}
      </div>
      <div className="mt-4 grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 p-4 h-44"><p className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500">Registration trend (daily, by UTM source)</p><div className="mt-6 h-24 flex items-end gap-1">{Array.from({ length: 30 }).map((_, i) => <div key={i} className="flex-1 bg-white/10" style={{ height: `${10 + ((i * 37) % 60)}%` }} />)}</div></div>
        <div className="rounded-2xl border border-white/10 p-4 h-44"><p className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500">By group</p>{["U11 Mixed", "U13 Boys", "U15 Boys", "Girls Only"].map((g) => <div key={g} className="mt-3 flex justify-between text-sm"><span className="text-slate-300">{g}</span><span className="font-mono-epl text-slate-500">0</span></div>)}</div>
      </div>
      <div className="mt-4 rounded-2xl border border-white/10 p-4 text-sm text-slate-400 flex items-center gap-2"><Lock size={14} className="accent-text" />Players table — search, filters (group, payment, size, consent), medical column visible only to authorised roles, CSV export.</div>
    </div>
  </div>
);

const Architecture = () => (
  <div className="grid lg:grid-cols-12 gap-10">
    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
      {ARCH.map((a) => <div key={a.t} className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"><p className="font-display text-xl font-bold tracking-tight accent-text">{a.t}</p><p className="mt-2 text-sm text-slate-400 leading-relaxed">{a.d}</p></div>)}
    </div>
    <div className="lg:col-span-5 space-y-8">
      <div><p className="eyebrow">Core collections</p><div className="mt-4 flex flex-wrap gap-2">{DATA_MODEL.map((d) => <span key={d} className="font-mono-epl text-[11px] rounded-full border border-white/10 px-3 py-1 text-slate-300">{d}</span>)}</div></div>
      <div><p className="eyebrow">Security & child safety</p><ul className="mt-4 space-y-2 text-sm text-slate-300">{SECURITY.map((s) => <li key={s} className="flex gap-3"><span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[var(--accent)] shrink-0" />{s}</li>)}</ul></div>
    </div>
  </div>
);

const Stripe = () => (
  <ol className="border-t border-white/10">
    {STRIPE_FLOW.map((s, i) => (
      <li key={s.t} data-testid={`stripe-step-${i}`} className="grid md:grid-cols-12 gap-4 py-6 border-b border-white/10">
        <span className="md:col-span-1 font-mono-epl accent-text">0{i + 1}</span>
        <p className="md:col-span-3 font-display text-xl font-bold tracking-tight">{s.t}</p>
        <p className="md:col-span-8 text-sm text-slate-400 leading-relaxed">{s.d}</p>
      </li>
    ))}
  </ol>
);

const Timeline = () => (
  <>
    <p className="mb-8 text-slate-300 max-w-3xl">Today is 14 October 2026 — 12 days before the advertising launch. That is tight but achievable for the launch scope below, provided the design is approved this week and launch content (bios, policies, tracking IDs) arrives by ~21 October. Anything not ready by then should not block ads; it ships in Phase 2, well before it's needed.</p>
    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
      {PHASES.map((p, i) => (
        <div key={p.name} data-testid={`phase-${i}`} className={`p-6 rounded-3xl border ${i === 0 ? "border-[var(--accent)]/50 bg-[var(--accent)]/[0.05]" : "border-white/10 bg-white/[0.03]"}`}>
          <p className="font-mono-epl text-xs accent-text">{p.when}</p>
          <p className="mt-2 font-display text-2xl font-bold tracking-tight">{p.name}</p>
          <ul className="mt-4 space-y-2 text-sm text-slate-400">{p.items.map((it) => <li key={it}>— {it}</li>)}</ul>
        </div>
      ))}
    </div>
    <p className="mt-6 text-xs text-slate-500">Data structures and permissions for attendance, feedback, media and multi-camp are built into Phase 1 so Phase 2 adds screens, not migrations.</p>
  </>
);

export default function Proposal() {
  return (
    <div data-testid="proposal-page" className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
      <header className="py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 text-sm text-slate-400 hover:text-white" data-testid="proposal-back-link"><ArrowLeft size={16} />Back to homepage</Link>
        <Logo />
      </header>
      <div className="py-16 md:py-24">
        <p className="eyebrow">Project proposal · for approval</p>
        <h1 className="mt-5 font-display font-extrabold tracking-[-0.04em] text-[1.9rem] sm:text-6xl lg:text-8xl leading-[0.95]">Elite Performance Lab<br /><span className="accent-text">Platform Blueprint</span></h1>
        <nav className="mt-10 flex flex-wrap gap-3 text-sm font-medium">
          {[["brand", "Design"], ["registration", "Registration"], ["admin", "Admin"], ["architecture", "Architecture"], ["stripe", "Stripe"], ["timeline", "Timeline"], ["needed", "What we need"]].map(([id, l]) => (
            <a key={id} href={`#${id}`} data-testid={`proposal-nav-${id}`} className="rounded-full border border-white/15 px-4 py-2 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors">{l}</a>
          ))}
        </nav>
      </div>
      <Block id="brand" n="01" title="Design & branding direction"><Brand /></Block>
      <Block id="registration" n="02" title="Registration flow"><Registration /></Block>
      <Block id="admin" n="03" title="Admin dashboard layout"><Admin /></Block>
      <Block id="architecture" n="04" title="Technical architecture"><Architecture /></Block>
      <Block id="stripe" n="05" title="Stripe integration approach"><Stripe /></Block>
      <Block id="timeline" n="06" title="Development timeline"><Timeline /></Block>
      <Block id="needed" n="07" title="What we need from you">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{NEEDED.map((n) => <p key={n} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-slate-300">{n}</p>)}</div>
        <Link to="/" className="btn-primary mt-12" data-testid="proposal-view-homepage">View homepage design</Link>
      </Block>
    </div>
  );
}
